import { Link } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { programLabel } from '@/lib/programs'
import type { Role, Task } from '@/lib/types'
import { sourceTemplate } from '@/lib/scientific'
import { TemplateLink } from '@/pages/scientific/TemplateSource'

/** Shared task chrome; example links stay within the introduction. */
export function TaskHeading({ task, role, backHref, editHref }: { task: Task; role: Role; backHref?: string; editHref?: string }) {
  const source = sourceTemplate(task)
  return <>
    <Link className="backlink" to={backHref ?? (role === 'sme' ? '/sme' : '/eng/tasks')}>
      ← {task.kind === 'scientific_workflow' || task.collection ? 'Workflows' : 'Tasks'}
    </Link>
    <div className="page-head">
      <div>
        <h1>{task.title}{role === 'engineer' && !task.collection && task.kind !== 'scientific_workflow' && <Link className="task-rollouts-link" to={`/eng/runs/tasks/${encodeURIComponent(task.task_id)}`} aria-label="View all task rollouts" title="View all task rollouts"><Icon name="arrow" size={20} /></Link>}</h1>
        <div className="task-meta">
          <span className="chip">{programLabel(task)}</span>
          <span>{task.sme_email}</span>
          {source && <TemplateLink source={source} developer={role === 'engineer'} />}
          {task.lifecycle === 'retired' && <span className="chip retired"><Icon name="archive" size={13} />Retired</span>}
        </div>
      </div>
      {task.lifecycle !== 'retired' && task.stage === 0 && role === 'sme' && (
        <Link className="btn" to={editHref ?? `/sme/tasks/${task.task_id}/edit`}>
          Edit draft
        </Link>
      )}
    </div>
  </>
}

export function TaskTabs({ stage, section, linkFor, items, scientific = false }: { stage: number; section: string; linkFor?: (section: string) => string; items?: string[]; scientific?: boolean }) {
  const sections = items ?? ['work', 'brief', 'rubric', 'coach', 'files', 'activity']
  const labels: Record<string, string> = {
    work: stage === 3 ? 'Build' : 'Work',
    brief: 'Brief',
    rubric: 'Rubric',
    coach: 'Coach',
    files: 'Files',
    activity: 'Activity',
  }
  return (
    <nav className="task-tabs" data-tour="task-sections" aria-label={scientific ? 'Workflow sections' : 'Task sections'}>
      {sections.map((item) => (
        <Link
          key={item}
          className={`task-tab ${section === item ? 'active' : ''}`}
          to={{ search: linkFor?.(item) ?? `?section=${item}` }}
        >
          {labels[item]}
        </Link>
      ))}
    </nav>
  )
}
